import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiService from "../services/api";
import { triggerBlobDownload } from "../utils/downloadUtils";
import ConfirmModal from "../components/ConfirmModal";
import { useAuth } from "../contexts/AuthContext";

import {
  TutorPayrollHeader,
  TutorPayrollLoadingState,
  TutorPayrollErrorState,
  PayrollSummary,
  LineItemsTable,
  AdjustmentsSection,
  TutorNotesSection,
} from "../components/tutor-payroll";

const TutorPayrollDetailPage = () => {
  const { yearMonth, tutorId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [payLoading, setPayLoading] = useState(false);
  const [confirmPay, setConfirmPay] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const formatMonth = (ym) => {
    if (!ym) return "N/A";
    const [year, month] = ym.split("-");
    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
  };

  // Fetch payroll run (for locked status)
  const { data: payrollRun } = useQuery({
    queryKey: ["payrollRun", yearMonth],
    queryFn: () => apiService.getPayrollRun(yearMonth),
    staleTime: 60 * 1000,
  });

  // Fetch all tutor summaries and find this one
  const {
    data: tutorSummary,
    isLoading: isLoadingSummary,
    error: summaryError,
  } = useQuery({
    queryKey: ["payrollTutors", yearMonth],
    queryFn: () => apiService.getPayrollTutors(yearMonth),
    select: (tutors) => tutors?.find((t) => t.canonicalTutorId === tutorId) || null,
    staleTime: 60 * 1000,
  });

  // Fetch all line items for this tutor (handles pagination automatically)
  const {
    data: allItems,
    isLoading: isLoadingItems,
    error: itemsError,
  } = useQuery({
    queryKey: ["payrollLineItems", yearMonth, tutorId],
    queryFn: () => apiService.getAllPayrollLineItems(yearMonth, tutorId),
    staleTime: 60 * 1000,
  });

  const isLoading = isLoadingSummary || isLoadingItems;
  const error = summaryError || itemsError;
  const allItemsList = allItems || [];
  const lineItems = allItemsList.filter((i) => i.lineType !== "adjustment");
  const adjustments = allItemsList.filter((i) => i.lineType === "adjustment");
  const tutorName = tutorSummary?.tutorName || `Tutor ${tutorId}`;
  const isLocked = payrollRun?.status === "LOCKED";
  const paymentStatus = tutorSummary?.paymentStatus; // DRAFT | LOCKED | PAID
  const isPaid = paymentStatus === "PAID";
  const isEmployee = tutorSummary?.isEmployee ?? false;
  const financialsMasked = isEmployee && !isSuperAdmin;

  const handleDownloadPdf = async () => {
    setPdfDownloading(true);
    try {
      const blob = await apiService.downloadTutorPdf(yearMonth, tutorId);
      const safeName = tutorName.replace(/[^a-zA-Z0-9_\- ]/g, "_");
      triggerBlobDownload(blob, `payroll_${yearMonth}_${safeName}.pdf`);
    } catch (err) {
      alert("PDF download failed: " + err.message);
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleTogglePaid = async () => {
    setPayLoading(true);
    try {
      if (isPaid) {
        await apiService.unmarkTutorPaid(yearMonth, tutorId);
      } else {
        await apiService.markTutorPaid(yearMonth, tutorId);
      }
      await queryClient.invalidateQueries({ queryKey: ["payrollTutors", yearMonth] });
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <div style={{ padding: "1.5rem", maxWidth: "1200px", margin: "0 auto" }}>
      <TutorPayrollHeader
        yearMonth={yearMonth}
        tutorName={tutorName}
        tutorEmail=""
        formatMonth={formatMonth}
        navigate={navigate}
      />

      {isLoading ? (
        <TutorPayrollLoadingState />
      ) : error ? (
        <TutorPayrollErrorState error={error} onRetry={() => window.location.reload()} />
      ) : (
        <>
          {financialsMasked && (
            <div style={{
              backgroundColor: "#fef9c3", border: "1px solid #fde68a",
              borderRadius: "0.375rem", padding: "0.75rem 1rem",
              marginBottom: "1rem", fontSize: "0.875rem", color: "#92400e",
            }}>
              Payroll summary and base pay details are restricted for employee tutors.
            </div>
          )}
          {!financialsMasked && (
            <PayrollSummary
              tutorSummary={tutorSummary}
              isLocked={isLocked}
              isPaid={isPaid}
              payLoading={payLoading}
              onTogglePaid={() => setConfirmPay(true)}
              showPdfButton={isLocked}
              onDownloadPdf={handleDownloadPdf}
              pdfDownloading={pdfDownloading}
            />
          )}
          <LineItemsTable lineItems={lineItems} />
          {!financialsMasked && (
            <AdjustmentsSection
              adjustments={adjustments}
              isLocked={isLocked}
              yearMonth={yearMonth}
              canonicalTutorId={tutorId}
            />
          )}
          <TutorNotesSection yearMonth={yearMonth} canonicalTutorId={tutorId} />
        </>
      )}

      <ConfirmModal
        isOpen={confirmPay}
        title={isPaid ? "Unmark as Paid" : "Mark as Paid"}
        message={
          isPaid
            ? `Are you sure you want to unmark ${tutorName} as paid?`
            : `Are you sure you want to mark ${tutorName} as paid?`
        }
        confirmLabel={isPaid ? "Yes, Unmark" : "Yes, Mark Paid"}
        confirmStyle={isPaid ? "danger" : "primary"}
        onConfirm={() => { setConfirmPay(false); handleTogglePaid(); }}
        onCancel={() => setConfirmPay(false)}
      />

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default TutorPayrollDetailPage;
